import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/toast'
import {
  useCreateClientContact,
  useUpdateClientContact,
} from '@/hooks/useClients'
import { getApiErrorMessage } from '@/services/api'
import { cn } from '@/lib/utils'
import {
  formatPhoneInput,
  formatPhoneNumber,
  isValidPhoneNumber,
  normalizePhoneNumber,
} from '@/lib/phone'
import type { Client, ClientContact, ClientContactPayload } from '@/types'

interface FormValues {
  full_name: string
  role: string
  phone: string
  email: string
}

interface Props {
  client: Client
  contact: ClientContact | null
  onClose: () => void
}

const fieldClass =
  'h-10 rounded-xl border-[#e7e9ef] bg-[#f4f5f8] px-4 py-3 text-sm placeholder:text-[#6c7285] focus-visible:ring-0 focus-visible:ring-offset-0'

export function ClientContactForm({ client, contact, onClose }: Props) {
  const { t } = useTranslation('clients')
  const create = useCreateClientContact(client.id)
  const update = useUpdateClientContact(client.id)
  const pending = create.isPending || update.isPending

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { full_name: '', role: '', phone: '', email: '' },
  })
  const phoneField = register('phone', {
    required: t('form.phoneRequired'),
    maxLength: 24,
    validate: value => isValidPhoneNumber(value) || t('form.phoneInvalid'),
  })

  useEffect(() => {
    reset({
      full_name: contact?.full_name ?? '',
      role: contact?.role ?? '',
      phone: formatPhoneNumber(contact?.phone),
      email: contact?.email ?? '',
    })
  }, [contact, reset])

  const onSubmit = async (values: FormValues) => {
    const body: ClientContactPayload = {
      phone: normalizePhoneNumber(values.phone),
      ...(values.email.trim() ? { email: values.email.trim() } : {}),
      full_name: values.full_name.trim() || undefined,
      role: values.role.trim() || undefined,
    }
    try {
      if (contact) {
        await update.mutateAsync({ contactId: contact.id, body })
      } else {
        await create.mutateAsync(body)
      }
      toast({ variant: 'success', title: t('form.contactSaved') })
      onClose()
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('form.contactSaveError')),
      })
    }
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className='flex flex-col gap-3 border-t border-dashed border-[#e7e9ef] pt-4'
    >
      <Input
        aria-label={t('form.contactName')}
        placeholder={t('form.contactName')}
        className={cn(fieldClass, errors.full_name && 'border-destructive')}
        {...register('full_name', {
          required: true,
        })}
      />
      <Input
        aria-label={t('form.role')}
        placeholder={t('form.roleInlinePlaceholder')}
        className={fieldClass}
        {...register('role')}
      />
      <Input
        type='tel'
        aria-label={t('form.phone')}
        placeholder={t('form.phone')}
        maxLength={24}
        className={cn(fieldClass, errors.phone && 'border-destructive')}
        {...phoneField}
        onChange={event => {
          event.target.value = formatPhoneInput(event.target.value)
          void phoneField.onChange(event)
        }}
      />
      {errors.phone && (
        <p className='text-xs text-destructive'>{errors.phone.message}</p>
      )}
      <Input
        type='email'
        aria-label={t('form.email')}
        placeholder={t('form.email')}
        className={cn(fieldClass, errors.email && 'border-destructive')}
        {...register('email', {
          validate: value =>
            !value.trim() ||
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()) ||
            'Please enter a valid email address.',
        })}
      />
      <div className='flex justify-end gap-3 pt-1'>
        <Button
          type='button'
          variant='outline'
          disabled={pending}
          onClick={onClose}
          className='h-10 rounded-xl px-4 text-sm font-medium'
        >
          {t('form.cancel')}
        </Button>
        <Button
          type='submit'
          disabled={pending}
          className='h-10 gap-2 rounded-xl bg-[#ff5a1f] px-4 text-sm font-medium text-white hover:bg-[#e6511c]'
        >
          <Check className='size-4' />
          {t('form.saveContact')}
        </Button>
      </div>
    </form>
  )
}
